import { Box, Flex, Image, Text, useMediaQuery } from '@chakra-ui/react';
import { Link } from 'gatsby';
import useSegment from 'hooks/useSegment';
import React, { useMemo, useState, useEffect } from 'react';
import { useCartStore } from 'stores/cart';
import AddToCartButton from '../../molecules/AddToCartButton/AddToCartButton';
import Checkbox from '../Checkbox/Checkbox';
import Icon from '../Icon/Icon';
import ReviewIcons from '../ReviewIcons/ReviewIcons';
import { getStorage } from '../../../helpers/general';
import * as styles from './NewProductCard.module.scss';
import { NewProductCardWrapper } from './NewProductCard.styled';

const NewProductCard = ({
  name,
  hasMoreOptions,
  image,
  selectable,
  selected,
  addToCart,
  rating,
  ratingCount,
  price,
  salePrice,
  url,
  productId,
  onSelect,
  variantId,
  inventoryLevel,
  customFields,
  autoshipDiscount,
  track,
  sku,
  modifiers = [],
  variants = [],
  buttonSpace = false,
  showVariants = false,
  landscape = false,
  removeRadius = false,
  wFull = false,
  slim = false,
  disabled = false,
  showIcon = false,
  skipCartModal = false,
  intercept = undefined,
  addAdditionalAction
}) => {
  const { setCartModal } = useCartStore(state => state);
  const { track: segmentTrack } = useSegment();
  const [defaultVariant, setDefautlVariant] = useState(variants[0]);
  const [isMobile] = useMediaQuery('(max-width: 800)', {
    ssr: true,
    fallback: false // return false on the server, and re-evaluate on the client side
  });
  const algoliaQueryId = getStorage('algoliaQueryId');

  useEffect(() => {
    if (variants.length && defaultVariant && defaultVariant.inventory_level === 0) {
      let foundInStockVar = false;
      variants.forEach(v => {
        if (v.inventory_level !== 0 && !foundInStockVar) {
          foundInStockVar = true;
          setDefautlVariant(v);
        }
      });
    }
  }, [variants]);
  const discount = useMemo(() => {
    let displayprice = 0;

    let _salePrice = salePrice;
    let _price = price;

    if (defaultVariant?.price && defaultVariant?.calculated_price) {
      _salePrice = defaultVariant.calculated_price;
      _price = defaultVariant.price;
    }

    if (_salePrice !== _price && _salePrice !== 0) {
      displayprice = (((_price - _salePrice) / _price) * 100).toFixed(0);
    }
    return displayprice;
  }, [salePrice, price, defaultVariant]);

  // Compute the autoship discount
  const autoship = useMemo(() => {
    let discountedPrice = 0;
    if (autoshipDiscount && defaultVariant) {
      const priceValue = defaultVariant?.calculated_price;
      const discountAmount = priceValue * (autoshipDiscount / 100);
      discountedPrice = priceValue - discountAmount;
    }

    return discountedPrice.toFixed(2);
  }, [defaultVariant, autoshipDiscount]);

  // Check if the product is out of stock
  const outOfStock = useMemo(() => {
    return inventoryLevel === 0 && track;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryLevel]);

  // Check if the product is a medication
  const isMedication = useMemo(() => {
    const findMedication = customFields?.find(
      field => field.name === 'Product Type' && field.value === 'Medication'
    );
    return !!findMedication;
  }, [customFields]);

  const trackAddToCart = () => {
    segmentTrack('Product Added', {
      product_id: productId,
      name: name,
      sku: sku,
      price: price,
      quantity: 1,
      product_type: isMedication ? 'Medication' : 'OTC',
      currency: 'AUD',
      variant: `(${variantId})`,
      url: url,
      image_url: image,
      algolia_query_id: algoliaQueryId
    });
  };

  // Check if the product is eligible for autoship
  // const isAutoship = useMemo(() => {
  //   const findAutoship = modifiers?.find(
  //     modifier => modifier.name === 'Ships Every'
  //   );
  //   return !!findAutoship;
  // }, [modifiers]);

  const displaySale = discount > 4;
  const navigateHandler = () => {
    if (intercept) {
      return;
    }
    setCartModal(false);

    // navigate(url);
  };

  const variantHTML = () => {
    if (outOfStock || !showVariants || variants.length < 2) return <></>;
    const onSelectedVariant = v => {
      if (v.inventory_level === 0 && track) return;
      setDefautlVariant(v);
      // navigate(`${url}?v=${defaultVariant?.id}`)
    };

    return (
      <div className="variants">
        <section>
          {variants.map((variant, index) => {
            const variantClassName = `${
              variant.inventory_level === 0 && track ? 'variant-out-of-stock' : ''
            } ${variant.id === defaultVariant.id ? 'selected' : ''}`;
            return (
              <>
                <button
                  key={index}
                  className={variantClassName}
                  onClick={() => onSelectedVariant(variant)}
                >
                  {variant.option_values?.[0].label}
                </button>
              </>
            );
          })}
        </section>
      </div>
    );
  };

  const priceHTML = () => {
    return (
      <div className={`price`}>
        <>
          <p className="price__actual">
            ${(defaultVariant?.calculated_price ?? (salePrice ? salePrice : price))?.toFixed(2)}
          </p>
          {(defaultVariant?.calculated_price !== defaultVariant?.price ||
            (salePrice !== 0 && salePrice !== price)) && (
            <p className="price__discount">${(defaultVariant?.price ?? price)?.toFixed(2)}</p>
          )}
        </>
      </div>
    );
  };

  return (
    <NewProductCardWrapper
      $isMobile={isMobile}
      key={`product-${productId}`}
    >
      <Box
        tabIndex={0}
        as="figure"
        borderRadius={removeRadius ? 0 : '10px'}
        m={0}
        height={!isMobile ? '' : 'auto'}
        position="relative"
        zIndex="1"
        _hover={
          !slim
            ? {
                boxShadow: '0px 0px 20px 0px rgba(32, 32, 32, 0.12)',
                transform: 'scale(1.01)',
                zIndex: '2',
                transition: 'all 0.3s ease'
              }
            : undefined
        }
        className={`${styles.root} ${landscape ? styles.landscape : ''} ${
          outOfStock && track ? 'out-of-stock' : ''
        }
      ${wFull ? 'w-full' : ''}
      ${slim ? 'slim' : ''}

      `}
      >
        <>
          {displaySale && !outOfStock && <p className={`discount`}>{discount}%</p>}
          {outOfStock && track && <p className="sold-out">Sold Out</p>}
        </>

        {selectable && (
          <Checkbox
            isChecked={selected}
            className={'select'}
            action={() => onSelect(productId, variantId, salePrice)}
          />
        )}
        <Link
          className="image-container"
          to={`${url}?v=${defaultVariant?.id}`}
          role="button"
          tabIndex={-1}
          onClick={navigateHandler}
        >
          <Image src={image ?? '/404.svg'} loading="lazy" alt={name} w={'full'} />
          <p className="caption"> {hasMoreOptions ? 'More options available' : ''}</p>
        </Link>

        <figcaption>
          <Text
            as={Link}
            to={`${url}?v=${defaultVariant?.id}`}
            onClick={navigateHandler}
            cursor={'pointer'}
            noOfLines={3}
            color={'var(--standard-black)'}
            title={name}
            className="product-title"
          >
            {name}
          </Text>
          <div className={`reviews`}>
            <ReviewIcons
              gap={'3px'}
              size="small"
              rating={Number.isNaN(rating / ratingCount) ? 0 : rating / ratingCount}
            />
            ({ratingCount})
          </div>

          {isMedication && (
            <Flex mb={4} gap={1} align={'center'}>
              <Icon symbol="prescription" />
              <Text
                fontSize={'14px'}
                color={'#006E95'}
              >
                Prescription medicine
              </Text>
            </Flex>
          )}
          {variantHTML()}
          {priceHTML()}
          {autoshipDiscount && autoshipDiscount > 0 ? (
            <div className={`autoship mb-4`}>
              <p className="autoship__price">${autoship}</p>
              <Icon symbol="auto-ship" />
            </div>
          ) : null}
          <div
            className={buttonSpace ? 'mt-6' : ''}
            style={
              addToCart
                ? {
                    marginTop: '10px'
                  }
                : isMobile
                ? {
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translate(-50%)',
                    top: 'none'
                  }
                : null
            }
          >
            <>
              {addToCart && (
                <AddToCartButton
                  tracker={trackAddToCart}
                  disabled={disabled}
                  skipCartModal={skipCartModal}
                  product={{
                    name,
                    price,
                    url
                  }}
                  productId={productId}
                  fullWidth
                  showIcon={showIcon}
                  variantId={variantId}
                  additionalAction={addAdditionalAction || null}
                >
                  {disabled ? 'ADDED TO CART' : 'ADD TO CART'}
                </AddToCartButton>
              )}
            </>
          </div>
        </figcaption>
      </Box>
    </NewProductCardWrapper>
  );
};

export default NewProductCard;
